import {
  BgColorsOutlined,
  CheckOutlined,
  DesktopOutlined,
  LogoutOutlined,
  SettingOutlined,
  SwapOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Space, Spin, Typography } from 'antd';
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '@/common/hooks/AppStateProvider';
import {
  resolveSwitchedOrganizationPath,
  useOrganizationPath,
} from '@/common/hooks/use-organization-path';
import { authClient } from '@/common/utils/auth-client';
import { THEME_OPTIONS } from '@/common/theme/use-theme';
import { useThemePreference } from '@/common/theme/ThemeContext';
import HeaderDropdown from './HeaderDropdown';

export const AvatarName = () => {
  const { session } = useAppContext();
  return <Typography.Text>{session.user?.name}</Typography.Text>;
};
export const AvatarDropdown = ({ menu, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId, resolveOrganizationPath } = useOrganizationPath();
  const { session } = useAppContext();
  const { themeName, setThemeName } = useThemePreference();
  const [open, setOpen] = useState(false);
  const organizations = useQuery({
    queryKey: ['user-menu-organizations', session.user?.id],
    enabled: Boolean(open && menu && organizationId),
    queryFn: async () => {
      const { data, error } = await authClient.organization.list();
      if (error) throw error;
      return data || [];
    },
  });
  const onMenuClick = (event) => {
    const { key } = event;
    if (key === 'reload-organizations') {
      organizations.refetch();
      return;
    }
    setOpen(false);
    if (key.startsWith('theme:')) {
      setThemeName(key.slice('theme:'.length));
      return;
    }
    if (key.startsWith('organization:')) {
      const nextOrganizationId = key.slice('organization:'.length);
      if (nextOrganizationId !== organizationId) {
        window.location.assign(
          resolveSwitchedOrganizationPath({
            currentOrganizationId: organizationId,
            nextOrganizationId,
            ...location,
          }),
        );
      }
      return;
    }
    if (key === 'logout') {
      session.logout().then(() => {
        navigate('/', { replace: true });
      });
      return;
    }
    if (key === 'harness') {
      navigate(resolveOrganizationPath('/harness'));
      return;
    }
    navigate(resolveOrganizationPath(`/account/${key}`));
  };
  const loading = (
    <Space size="small">
      <Spin size="small" />
    </Space>
  );
  if (!session.user?.name) {
    return loading;
  }
  let organizationItems = organizations.data?.map((organization) => ({
    key: `organization:${organization.id}`,
    role: 'menuitemradio',
    'aria-checked': organization.id === organizationId,
    label: organization.name || organization.id,
    extra: organization.id === organizationId ? <CheckOutlined aria-hidden /> : undefined,
  }));
  if (organizations.isError) {
    organizationItems = [{ key: 'reload-organizations', label: '加载失败，重试' }];
  } else if (!organizationItems?.length) {
    organizationItems = [
      {
        key: 'organizations-status',
        label: organizations.isPending ? '加载中' : '暂无组织',
        disabled: true,
      },
    ];
  }
  const menuItems = [
    ...(menu
      ? [
          {
            key: 'settings',
            icon: <SettingOutlined />,
            label: '个人信息',
          },
          {
            key: 'organizations',
            icon: <TeamOutlined />,
            label: '组织管理',
          },
          ...(organizationId
            ? [
                { key: 'harness', icon: <DesktopOutlined />, label: '智能助手' },
                {
                  key: 'switch-organization',
                  icon: <SwapOutlined />,
                  label: '切换组织',
                  children: organizationItems,
                },
              ]
            : []),
        ]
      : []),
    {
      key: 'switch-theme',
      icon: <BgColorsOutlined />,
      label: '切换主题',
      children: THEME_OPTIONS.map(({ label, value }) => ({
        key: `theme:${value}`,
        role: 'menuitemradio',
        'aria-checked': value === themeName,
        label,
        extra: value === themeName ? <CheckOutlined aria-hidden /> : undefined,
      })),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];
  return (
    <HeaderDropdown
      open={open}
      onOpenChange={(nextOpen, info) => {
        if (info.source === 'trigger') setOpen(nextOpen);
      }}
      menu={{
        selectedKeys: [],
        triggerSubMenuAction: 'click',
        onClick: onMenuClick,
        items: menuItems,
      }}
      trigger={['click']}
    >
      {children}
    </HeaderDropdown>
  );
};
